import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, Package } from 'lucide-react';
import { getConfidenceStyle, deriveInItembal, formatDate, formatUnitPrice } from '../../utils/formatting';
import { apiService } from '../../services/apiService';

export default function ItemSearchModal({ item, itemIndex, customerNumber, shiptoNumber, warehouseId, correlationId, user, onSelect, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  // Mirrors A+'s "one warehouse / all warehouses" choice (Tonya, 2026-07-30). Default to this
  // warehouse only: a description search otherwise returns items from every warehouse that the
  // CSR can't order here, each flagged "Not set up in ITEMBAL". Only offered when we actually
  // know the warehouse — with none resolved there's nothing to scope to.
  const [warehouseOnly, setWarehouseOnly] = useState(true);
  const canScopeToWarehouse = !!warehouseId;
  const scopeToWarehouse = canScopeToWarehouse && warehouseOnly;
  // A+ Order Pad: search only what this customer has bought (last 5 years, customer-wide). On by
  // default when the customer is known; a blank search lists the whole history (Tonya, 2026-10-14).
  const [historyOnly, setHistoryOnly] = useState(true);
  const canUseHistory = !!customerNumber;
  const useHistory = canUseHistory && historyOnly;
  const inputRef = useRef(null);

  // Auto-focus the search input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim() && !useHistory) {
      setResults([]);
      setHasSearched(false);
      setError(null);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.searchItems(
          query.trim(),
          customerNumber,
          shiptoNumber,
          correlationId,
          user?.userId,
          user?.userEmail,
          warehouseId,
          scopeToWarehouse,
          useHistory
        );
        // Surface items set up in the selected warehouse first (Array.sort is stable, so the
        // existing confidence order is preserved within each group). Items whose setup is
        // UNKNOWN (null — the ITBAL lookup failed) rank between set-up and definitely-not-set-up:
        // we can't claim they're unavailable, so don't bury them at the bottom.
        const setUpRank = r => {
          const state = deriveInItembal(r.in_itembal, r.inventory);
          return state === true ? 2 : state === null ? 1 : 0;
        };
        const sorted = [...(data.results || [])].sort((a, b) => setUpRank(b) - setUpRank(a));
        setResults(sorted);
        setHasSearched(true);
      } catch (err) {
        console.error('Item search failed:', err);
        setError(err.message || 'Search failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, query.trim() ? 400 : 0);

    return () => clearTimeout(timer);
  }, [query, customerNumber, shiptoNumber, warehouseId, correlationId, user, scopeToWarehouse, useHistory]);

  const handleSelect = (result) => {
    onSelect(itemIndex, {
      item_number: result.item_number,
      description: result.description,
      unit_of_measure: result.unit_of_measure,
      val_price_unit_of_measure: result.val_price_unit_of_measure,
      val_price_unit_conversion: result.val_price_unit_conversion,
      inventory: result.inventory || null,
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center animate-fadeIn"
    >
      <div
        className="bg-slate-800 border border-slate-600 rounded-xl shadow-2xl w-full max-w-lg mx-4 animate-scaleIn flex flex-col max-h-[80vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Search className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Search A+ Items</h3>
              <p className="text-xs text-slate-400">
                Line {item?.poLineNumber || (itemIndex + 1)}
                {item?.poDescription && <> &mdash; {item.poDescription}</>}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Input */}
        <div className="px-5 py-3 border-b border-slate-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={useHistory
                ? "Filter this customer's items by number, description, or their part #..."
                : 'Search by item number or description...'}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-10 pr-10 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 transition-colors"
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400 animate-spin" />
            )}
            {!loading && query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Customer history — mirrors A+'s Order Pad. */}
          {canUseHistory && (
            <label className="mt-2.5 flex items-center gap-1.5 cursor-pointer text-xs w-fit">
              <input
                type="checkbox"
                checked={historyOnly}
                onChange={e => setHistoryOnly(e.target.checked)}
                className="accent-amber-500"
              />
              <span className={historyOnly ? 'text-white' : 'text-slate-400'}>
                Only items this customer has bought <span className="text-slate-500">(last 5 years)</span>
              </span>
            </label>
          )}

          {/* Warehouse scope — mirrors A+'s one-warehouse / all-warehouses option. */}
          {canScopeToWarehouse && (
            <div className="mt-2.5 flex items-center gap-4">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input
                  type="radio"
                  name="itemSearchScope"
                  checked={warehouseOnly}
                  onChange={() => setWarehouseOnly(true)}
                  className="accent-amber-500"
                />
                <span className={warehouseOnly ? 'text-white' : 'text-slate-400'}>
                  WH {warehouseId} only
                </span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input
                  type="radio"
                  name="itemSearchScope"
                  checked={!warehouseOnly}
                  onChange={() => setWarehouseOnly(false)}
                  className="accent-amber-500"
                />
                <span className={!warehouseOnly ? 'text-white' : 'text-slate-400'}>
                  All warehouses
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-auto min-h-0">
          {error && (
            <div className="px-5 py-4 text-sm text-red-400">{error}</div>
          )}

          {!error && hasSearched && results.length === 0 && !loading && (
            <div className="px-5 py-8 text-center">
              <Package className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              {useHistory ? (
                // Nothing in this customer's history — offer the full catalog rather than a dead end.
                <>
                  <p className="text-sm text-slate-400">
                    {query.trim()
                      ? `This customer hasn't bought anything matching "${query.trim()}" in the last 5 years`
                      : 'No purchases by this customer in the last 5 years'}
                    {scopeToWarehouse ? ` that is set up in WH ${warehouseId}` : ''}
                  </p>
                  <button
                    onClick={() => setHistoryOnly(false)}
                    className="mt-2 text-xs text-amber-400 hover:text-amber-300 underline"
                  >
                    Search all A+ items instead
                  </button>
                </>
              ) : scopeToWarehouse ? (
                // Don't leave the CSR at a dead end: the match may exist but be set up elsewhere,
                // and that's invisible while the search is scoped.
                <>
                  <p className="text-sm text-slate-400">
                    No items set up in WH {warehouseId} for "{query}"
                  </p>
                  <button
                    onClick={() => setWarehouseOnly(false)}
                    className="mt-2 text-xs text-amber-400 hover:text-amber-300 underline"
                  >
                    Search all warehouses instead
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-400">No items found for "{query}"</p>
                  <p className="text-xs text-slate-500 mt-1">Try a different item number or description</p>
                </>
              )}
            </div>
          )}

          {!error && !hasSearched && !loading && (
            <div className="px-5 py-8 text-center">
              <Search className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-400">Type to search the A+ item catalog</p>
              <p className="text-xs text-slate-500 mt-1">Search by item number, description, or keywords</p>
            </div>
          )}

          {results.length > 0 && (
            <div className="divide-y divide-slate-700/50">
              {results.map((result, idx) => {
                const style = getConfidenceStyle(result.confidence_level, result.confidence);
                // Only azure_search produces a real score. itmst/iaxrf hits are hardcoded
                // confidence 1.0 (item_validator.search), so rendering "100%" on them adds no
                // information beyond the EXACT / CROSS-REF tag — and actively misleads: the CSR
                // reads it as "100% the right item for this PO line" when it only ever meant
                // "exact hit on the text you typed". Nothing in this modal compares results
                // against the PO line at all. Percentage shown for scored matches only.
                const hasRealScore = result.match_source === 'azure_search';
                const itembalState = deriveInItembal(result.in_itembal, result.inventory);
                const setUp = itembalState === true;
                const setUpUnknown = itembalState === null;
                const onHand = result.inventory?.quantity_on_hand ?? 0;
                // Warehouses actually holding stock. Falls back to warehouse_count for responses
                // from a backend that predates stocked_warehouse_count.
                const stockedCount = result.inventory?.stocked_warehouse_count
                  ?? result.inventory?.warehouse_count ?? 0;
                return (
                  <button
                    key={idx}
                    onClick={() => handleSelect(result)}
                    className="w-full text-left px-5 py-3 hover:bg-slate-700/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-white text-sm">{result.item_number}</span>
                          {result.match_source === 'itmst' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 uppercase tracking-wider">Exact</span>
                          )}
                          {result.match_source === 'iaxrf' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 uppercase tracking-wider">Cross-Ref</span>
                          )}
                          {/* Literal substring hit from the item master. No percentage: its score
                              is a fixed heuristic, so a number would be false precision. */}
                          {result.match_source === 'itmst_description' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/25 text-slate-300 uppercase tracking-wider">Description</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-300 mt-0.5 truncate">{result.description}</p>
                        <p className="text-xs text-slate-500 mt-0.5">UOM: {result.unit_of_measure || '—'}</p>
                        {/* Last invoice line for this customer. Price is per the pricing U/M, as A+ stores it. */}
                        {result.last_purchase?.date && (
                          <p className="text-xs mt-0.5 text-sky-300/90">
                            Last bought {formatDate(result.last_purchase.date)}
                            {result.last_purchase.quantity != null && (
                              <> &middot; {Number(result.last_purchase.quantity).toLocaleString()} {result.last_purchase.unit || ''}</>
                            )}
                            {result.last_purchase.price != null && (
                              <> @ {formatUnitPrice(result.last_purchase.price)}{result.last_purchase.price_unit ? `/${result.last_purchase.price_unit}` : ''}</>
                            )}
                          </p>
                        )}
                        {/* Labels must describe whatever the CSR asked about. Scoped, they're about
                            the selected warehouse. In "All warehouses" mode the backend reports
                            totals across every warehouse plus the one holding the most, so naming
                            the selected warehouse here would be misleading — an item stocked in
                            WH 08 previously read "Not set up in ITEMBAL (WH 33)". */}
                        {setUpUnknown ? (
                          // ITBAL lookup failed — don't assert either way.
                          <p className="text-xs mt-0.5 text-slate-400">
                            Setup not verified{scopeToWarehouse && warehouseId ? ` (WH ${warehouseId})` : ''}
                          </p>
                        ) : !setUp ? (
                          <p className="text-xs mt-0.5 text-red-400 font-medium">
                            {scopeToWarehouse
                              ? `Not set up in ITEMBAL${warehouseId ? ` (WH ${warehouseId})` : ''}`
                              : 'Not stocked in any warehouse'}
                          </p>
                        ) : onHand > 0 ? (
                          <>
                            <p className="text-xs mt-0.5 text-emerald-500">
                              {`${Number(onHand).toLocaleString()} ${result.unit_of_measure || ''} in stock`.trim()}
                              {result.inventory?.warehouse_id && (
                                <span className="text-slate-500 ml-1">
                                  {/* Count warehouses that actually HOLD stock. warehouse_count
                                      includes empty ITBAL rows, so it read "across 4 WHs" for an
                                      item sitting entirely in one — the confusing part. */}
                                  {scopeToWarehouse || !(stockedCount > 1)
                                    ? `(WH: ${result.inventory.warehouse_id})`
                                    : `(total across ${stockedCount} warehouses)`}
                                </span>
                              )}
                            </p>
                            {/* In "All warehouses" mode a total plus one warehouse doesn't tell the
                                CSR where the stock actually is. Show the real split so they can act
                                on it (Tonya, 2026-08-13). */}
                            {!scopeToWarehouse && stockedCount > 1 && result.inventory?.by_warehouse?.length > 1 && (
                              <p className="text-[11px] mt-0.5 text-slate-400 leading-snug">
                                {result.inventory.by_warehouse.map((w, i) => (
                                  <span key={w.warehouse_id}>
                                    {i > 0 && <span className="text-slate-600"> · </span>}
                                    <span className={w.warehouse_id === warehouseId ? 'text-emerald-400 font-medium' : ''}>
                                      WH {w.warehouse_id}: {Number(w.quantity).toLocaleString()}
                                    </span>
                                  </span>
                                ))}
                                {stockedCount > result.inventory.by_warehouse.length && (
                                  <span className="text-slate-600">
                                    {` +${stockedCount - result.inventory.by_warehouse.length} more`}
                                  </span>
                                )}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-xs mt-0.5 text-amber-400">
                            {scopeToWarehouse
                              ? `Out of stock — will backorder${warehouseId ? ` (WH ${warehouseId})` : ''}`
                              : 'Set up but out of stock everywhere — will backorder'}
                          </p>
                        )}
                      </div>
                      {hasRealScore && (
                        <span
                          className={`text-xs whitespace-nowrap ${style.color}`}
                          title="How closely this matches your search text — not a match to the PO line"
                        >
                          {Math.round(result.confidence * 100)}%
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
